import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Divider,
  MenuItem,
} from '@mui/material';
import { firmService, FirmRegistrationData } from '../services/firmService';

const FirmRegistration: React.FC = () => {
  const [formData, setFormData] = useState({
    // User Details
    organisation_name: '',
    full_name: '',
    email: '',
    job_title: '',
    is_authorized_signatory: false,
    
    // Contact Details
    address_1: '',
    address_2: '',
    zip_code: '',
    state: '',
    city: '',
    country: 'United States',
    contact: '',
    website: '',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const navigate = useNavigate();
  
  // Refs for form fields to enable scrolling
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});

  // Scroll to top when success message is displayed
  useEffect(() => {
    if (success) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [success]);

  const states = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming'
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Function to scroll to the first field with an error
  const scrollToFirstError = (errors: Record<string, string[]>) => {
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField && fieldRefs.current[firstErrorField]) {
      fieldRefs.current[firstErrorField]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      // Focus the field as well
      const element = fieldRefs.current[firstErrorField] as HTMLInputElement;
      if (element && element.focus) {
        setTimeout(() => element.focus(), 300);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setFieldErrors({});
    setLoading(true);

    try {
      // Transform form data to match backend expectations
      const registrationData: FirmRegistrationData = {
        name: formData.organisation_name,
        type: 'client' as const, // Default to client as specified
        address_1: formData.address_1,
        address_2: formData.address_2,
        zip_code: formData.zip_code,
        state: formData.state,
        city: formData.city,
        country: formData.country,
        contact_phone: formData.contact,
        website: formData.website,
        is_authorized_signatory: formData.is_authorized_signatory,
        registration_data: {
          full_name: formData.full_name,
          email: formData.email,
          job_title: formData.job_title,
        }
      };

      await firmService.registerFirm(registrationData);
      setSuccess('Firm registration submitted successfully! Please wait for admin approval.');
      
      // Clear form after successful submission
      setFormData({
        organisation_name: '',
        full_name: '',
        email: '',
        job_title: '',
        is_authorized_signatory: false,
        address_1: '',
        address_2: '',
        zip_code: '',
        state: '',
        city: '',
        country: 'United States',
        contact: '',
        website: '',
      });
    } catch (err: any) {
      console.log('Full error object:', err);
      console.log('Error response:', err.response);
      console.log('Error response data:', err.response?.data);
      
      // Parse validation errors from API response
      if (err.response && err.response.data && err.response.data.errors) {
        const apiErrors = err.response.data.errors;
        console.log('API errors:', apiErrors);
        setFieldErrors(apiErrors);
        scrollToFirstError(apiErrors);
        setError('Please correct the errors below and try again.');
      } else {
        console.log('Non-validation error:', err.message);
        setError(err.message || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 4,
          marginBottom: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h4" gutterBottom>
            Firm Registration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Register your organization with PMP
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {/* User Details Section */}
            <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
              User Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                required
                fullWidth
                name="organisation_name"
                label="Organisation Name *"
                value={formData.organisation_name}
                onChange={handleChange}
                error={!!fieldErrors.organisation_name}
                helperText={fieldErrors.organisation_name ? fieldErrors.organisation_name[0] : ''}
                inputRef={(el) => fieldRefs.current.organisation_name = el}
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  required
                  sx={{ flex: '1 1 300px' }}
                  name="full_name"
                  label="Full Name *"
                  value={formData.full_name}
                  onChange={handleChange}
                  error={!!fieldErrors.full_name}
                  helperText={fieldErrors.full_name ? fieldErrors.full_name[0] : ''}
                  inputRef={(el) => fieldRefs.current.full_name = el}
                />
                <TextField
                  required
                  sx={{ flex: '1 1 300px' }}
                  name="email"
                  label="Email *"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!fieldErrors.email}
                  helperText={fieldErrors.email ? fieldErrors.email[0] : ''}
                  inputRef={(el) => fieldRefs.current.email = el}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  sx={{ flex: '1 1 300px' }}
                  name="job_title"
                  label="Job Title"
                  value={formData.job_title}
                  onChange={handleChange}
                  error={!!fieldErrors.job_title}
                  helperText={fieldErrors.job_title ? fieldErrors.job_title[0] : ''}
                  inputRef={(el) => fieldRefs.current.job_title = el}
                />
                <Box sx={{ flex: '1 1 300px' }} />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    name="is_authorized_signatory"
                    checked={formData.is_authorized_signatory}
                    onChange={handleChange}
                  />
                }
                label="I am Authorized Signatory"
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Contact Details Section */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Contact Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                required
                fullWidth
                name="address_1"
                label="Address 1 *"
                value={formData.address_1}
                onChange={handleChange}
                error={!!fieldErrors.address_1}
                helperText={fieldErrors.address_1 ? fieldErrors.address_1[0] : ''}
                inputRef={(el) => fieldRefs.current.address_1 = el}
              />
              <TextField
                fullWidth
                name="address_2"
                label="Address 2"
                value={formData.address_2}
                onChange={handleChange}
                error={!!fieldErrors.address_2}
                helperText={fieldErrors.address_2 ? fieldErrors.address_2[0] : ''}
                inputRef={(el) => fieldRefs.current.address_2 = el}
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  required
                  sx={{ flex: '1 1 300px' }}
                  name="zip_code"
                  label="ZIP Code *"
                  value={formData.zip_code}
                  onChange={handleChange}
                  error={!!fieldErrors.zip_code}
                  helperText={fieldErrors.zip_code ? fieldErrors.zip_code[0] : ''}
                  inputRef={(el) => fieldRefs.current.zip_code = el}
                />
                <TextField
                  required
                  sx={{ flex: '1 1 300px' }}
                  select
                  name="state"
                  label="State *"
                  value={formData.state}
                  onChange={handleChange}
                  error={!!fieldErrors.state}
                  helperText={fieldErrors.state ? fieldErrors.state[0] : ''}
                  inputRef={(el) => fieldRefs.current.state = el}
                >
                  <MenuItem value="">
                    <em>- Select -</em>
                  </MenuItem>
                  {states.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  required
                  sx={{ flex: '1 1 300px' }}
                  name="city"
                  label="City *"
                  value={formData.city}
                  onChange={handleChange}
                  error={!!fieldErrors.city}
                  helperText={fieldErrors.city ? fieldErrors.city[0] : ''}
                  inputRef={(el) => fieldRefs.current.city = el}
                />
                <TextField
                  required
                  sx={{ flex: '1 1 300px' }}
                  name="country"
                  label="Country *"
                  value={formData.country}
                  onChange={handleChange}
                  error={!!fieldErrors.country}
                  helperText={fieldErrors.country ? fieldErrors.country[0] : ''}
                  inputRef={(el) => fieldRefs.current.country = el}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  sx={{ flex: '1 1 300px' }}
                  name="contact"
                  label="Contact"
                  value={formData.contact}
                  onChange={handleChange}
                  error={!!fieldErrors.contact}
                  helperText={fieldErrors.contact ? fieldErrors.contact[0] : ''}
                  inputRef={(el) => fieldRefs.current.contact = el}
                />
                <TextField
                  sx={{ flex: '1 1 300px' }}
                  name="website"
                  label="Website"
                  value={formData.website}
                  onChange={handleChange}
                  error={!!fieldErrors.website}
                  helperText={fieldErrors.website ? fieldErrors.website[0] : ''}
                  inputRef={(el) => fieldRefs.current.website = el}
                />
              </Box>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 4, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Submit Registration'}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
              onClick={() => navigate('/login')}
              disabled={loading}
            >
              Go back to login
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default FirmRegistration;
